import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./GanttStudioPage.css";
import { apiGet, apiPost, apiPut, createJobPoller, downloadFile, forgetRememberedJob, readRememberedJob, rememberJob } from "../apiClient";
import {
  ACTIVE_JOB_STATUSES,
  GANTT_SOURCE_PROJECT,
  GANTT_SOURCE_WORKSPACE,
  PROFILE_TYPE_HUMAN
} from "../domainCatalog";
import GanttAssistant from "./ganttStudio/GanttAssistant";
import GanttChart from "./ganttStudio/GanttChart";
import GanttEditor from "./ganttStudio/GanttEditor";
import GanttHeader from "./ganttStudio/GanttHeader";
import {
  addDays,
  buildWeeks,
  currentIsoDate,
  dateFromPointer,
  draftOnly,
  editable,
  emptyGantt,
  estimatedEffortHoursFromDates,
  jobStatusMessage,
  reflowFromEffortChange,
} from "./ganttStudio/ganttUtils";

const GANTT_GENERATION_JOB_KEY = "taskit.ganttGenerationJob";

const GanttStudioPage = ({ token, model, onUsageChange }) => {
  const pageRef = useRef(null);
  const [gantt, setGantt] = useState(emptyGantt);
  const [saved, setSaved] = useState(emptyGantt);
  const [team, setTeam] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [chat, setChat] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [memberFilter, setMemberFilter] = useState("all");
  const [workstreamFilter, setWorkstreamFilter] = useState("all");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [assistantHidden, setAssistantHidden] = useState(false);
  const [resizing, setResizing] = useState(null);
  const [generationJob, setGenerationJob] = useState(null);
  const today = currentIsoDate();
  const dirty = JSON.stringify(gantt) !== JSON.stringify(saved);
  const workspaceView = true;
  const activeGenerationJobId = generationJob && ACTIVE_JOB_STATUSES.includes(generationJob.status)
    ? generationJob.id
    : null;
  const selected = gantt.items.find((item) => item.id === selectedId);
  const humanTeam = useMemo(
    () => team.filter((member) => (member.profile_type || PROFILE_TYPE_HUMAN) === PROFILE_TYPE_HUMAN),
    [team]
  );
  const taskStudioManaged = [GANTT_SOURCE_PROJECT, GANTT_SOURCE_WORKSPACE].includes(gantt.source_type);
  const workstreams = useMemo(() => {
    const entries = (gantt.deadlines || []).map((deadline) => [deadline.workstream_key, deadline.workstream_name]);
    gantt.items.forEach((item) => {
      if (item.workstream_key && item.workstream_name) entries.push([item.workstream_key, item.workstream_name]);
    });
    return Array.from(new Map(entries).entries()).map(([key, name]) => ({ key, name }));
  }, [gantt.deadlines, gantt.items]);
  const visibleItems = useMemo(() => gantt.items.filter((item) => {
    const matchesMember = memberFilter === "all" || (item.assignee_ids || []).includes(Number(memberFilter));
    const itemWorkstream = item.workstream_key || (workstreams.length === 1 ? workstreams[0].key : null);
    const matchesWorkstream = workstreamFilter === "all" || itemWorkstream === workstreamFilter;
    return matchesMember && matchesWorkstream;
  }), [gantt.items, memberFilter, workstreamFilter, workstreams]);
  const weeks = useMemo(() => buildWeeks(gantt, today), [gantt, today]);
  const timelineStart = weeks[0];
  const timelineEnd = weeks.length ? addDays(weeks[weeks.length - 1], 6) : gantt.end_date;
  const resizeItemDate = useCallback((id, edge, value) => setGantt((current) => ({
    ...current,
    items: current.items.map((item) => {
      if (item.id !== id) return item;
      const revised = edge === "start"
        ? { ...item, start_date: value <= item.end_date ? value : item.end_date }
        : { ...item, end_date: value >= item.start_date ? value : item.start_date };
      return { ...revised, effort_hours: estimatedEffortHoursFromDates(revised, humanTeam) };
    })
  })), [humanTeam]);

  const load = useCallback(async () => {
    try {
      const [ganttData, teamData] = await Promise.all([
        apiGet("/gantt?scope=workspace", { token }),
        apiGet("/team", { token }).catch(() => null)
      ]);
      if (ganttData) {
        const next = editable(ganttData);
        setGantt(next); setSaved(next);
      }
      if (teamData) setTeam(teamData.members);
    } catch (error) { setMessage(error.message || "Could not load Gantt Studio."); }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const remembered = readRememberedJob(GANTT_GENERATION_JOB_KEY);
    if (!remembered?.id) return;
    setGenerationJob(remembered);
    setLoading(true);
    setMessage("Resuming Gantt generation job...");
  }, []);

  useEffect(() => {
    const handleFullscreen = () => setIsFullscreen(document.fullscreenElement === pageRef.current);
    document.addEventListener("fullscreenchange", handleFullscreen);
    return () => document.removeEventListener("fullscreenchange", handleFullscreen);
  }, []);

  useEffect(() => {
    if (selectedId && !visibleItems.some((item) => item.id === selectedId)) setSelectedId(null);
  }, [selectedId, visibleItems]);

  useEffect(() => {
    if (workstreamFilter !== "all" && !workstreams.some((workstream) => workstream.key === workstreamFilter)) {
      setWorkstreamFilter("all");
    }
  }, [workstreamFilter, workstreams]);

  useEffect(() => {
    if (isFullscreen) setAssistantHidden(false);
  }, [isFullscreen]);

  useEffect(() => {
    if (!resizing) return undefined;
    const handleMove = (event) => {
      const date = dateFromPointer(event, resizing.bounds, timelineStart, timelineEnd);
      if (date) resizeItemDate(resizing.id, resizing.edge, date);
    };
    const handleUp = () => setResizing(null);
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [resizing, timelineStart, timelineEnd, resizeItemDate]);

  const request = async (operation, workingMessage) => {
    setLoading(true); setMessage(workingMessage);
    try {
      return await operation();
    } finally { setLoading(false); }
  };

  const loadGenerationJob = useCallback(async (jobId) => {
    if (!jobId) return null;
    try {
      const data = await apiGet(`/jobs/${jobId}`, { token });
      setGenerationJob(data);
      setMessage(jobStatusMessage(data, "Gantt generation"));
      if (!ACTIVE_JOB_STATUSES.includes(data.status)) {
        forgetRememberedJob(GANTT_GENERATION_JOB_KEY);
        setLoading(false);
        if (data.status === "completed") {
          const next = editable(data.result_payload);
          setGantt(next); setSaved(next); setSelectedId(null);
          setMessage("Workspace Gantt generated and saved from the latest project data.");
          onUsageChange?.();
        } else if (data.status === "failed") {
          setMessage(data.error_message || "Gantt generation failed.");
        }
      }
      return data;
    } catch (error) {
      forgetRememberedJob(GANTT_GENERATION_JOB_KEY);
      setLoading(false);
      setMessage(error.message || "Error loading Gantt generation job.");
      return null;
    }
  }, [onUsageChange, token]);

  useEffect(() => {
    if (!activeGenerationJobId) return undefined;
    return createJobPoller(
      () => loadGenerationJob(activeGenerationJobId),
      { shouldContinue: (job) => Boolean(job && ACTIVE_JOB_STATUSES.includes(job.status)), immediate: true }
    );
  }, [activeGenerationJobId, loadGenerationJob]);

  const generate = async () => {
    try {
      const query = `?start_date=${gantt.start_date}&end_date=${gantt.end_date}`;
      const job = await request(() => apiPost(`/gantt/generate${query}`, undefined, { token, model }), "Queueing Gantt generation...");
      setGenerationJob(job);
      rememberJob(GANTT_GENERATION_JOB_KEY, { id: job.id, status: job.status });
      setLoading(true);
      setMessage("Gantt generation queued.");
    } catch (error) { setMessage(error.message); }
  };

  const save = async () => {
    try {
      const data = await request(() => apiPut("/gantt", gantt, { token }), "Saving latest Gantt version...");
      const next = editable(data); setGantt(next); setSaved(next);
      setMessage("Latest Gantt version saved.");
    } catch (error) { setMessage(error.message); }
  };

  const downloadExcel = async () => {
    if (dirty) {
      setMessage("Save the latest Gantt version before downloading Excel.");
      return;
    }
    setLoading(true); setMessage("Preparing Excel download...");
    try {
      await downloadFile("/gantt/export/xlsx", { token, filename: "project-gantt.xlsx" });
      setMessage("Excel download ready.");
    } catch (error) { setMessage(error.message); }
    finally { setLoading(false); }
  };

  const askAssistant = async () => {
    if (!instruction.trim() || !gantt.items.length) return;
    const text = instruction.trim(); setInstruction(""); setChat((items) => [...items, { role: "user", content: text }]);
    try {
      const data = await request(() => apiPost("/gantt/assistant", { gantt: draftOnly(gantt), instruction: text }, { token, model }), "The assistant is revising the schedule...");
      setGantt((current) => ({ ...editable(data.revised_gantt), source_type: current.source_type }));
      setChat((items) => [...items, { role: "assistant", content: data.message }]);
      setMessage("Assistant changes applied as an unsaved draft."); onUsageChange?.();
    } catch (error) { setChat((items) => [...items, { role: "assistant", content: error.message }]); }
  };

  const update = (field, value) => setGantt((current) => ({ ...current, [field]: value }));
  const updateItem = (id, field, value) => setGantt((current) => ({
    ...current,
    items: current.items.map((item) => item.id === id ? { ...item, [field]: value } : item)
  }));
  const updateEffortHours = (id, value) => setGantt((current) => ({
    ...current,
    items: reflowFromEffortChange(current.items, id, value, humanTeam)
  }));
  const startResize = (event, item, edge) => {
    event.preventDefault();
    event.stopPropagation();
    const row = event.currentTarget.closest(".gantt-timeline-row");
    const bounds = row?.getBoundingClientRect();
    if (!bounds) return;
    setSelectedId(item.id);
    setResizing({ id: item.id, edge, bounds });
    const date = dateFromPointer(event, bounds, timelineStart, timelineEnd);
    if (date) resizeItemDate(item.id, edge, date);
  };
  const deleteEpic = () => { setGantt((current) => ({ ...current, items: current.items.filter((item) => item.id !== selectedId) })); setSelectedId(null); };
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (pageRef.current?.requestFullscreen) await pageRef.current.requestFullscreen();
      else setMessage("Full-screen mode is not supported by this browser.");
    } catch (error) { setMessage(error.message || "Could not change full-screen mode."); }
  };

  return <div ref={pageRef} className={`gantt-studio-page ${isFullscreen ? "fullscreen" : ""}`}>
    <GanttHeader
      dirty={dirty}
      hasItems={Boolean(gantt.items.length)}
      isFullscreen={isFullscreen}
      loading={loading}
      workspaceView={workspaceView}
      onDownloadExcel={downloadExcel}
      onGenerate={generate}
      onSave={save}
      onToggleFullscreen={toggleFullscreen}
    />
    {message && <div className="gantt-message">{message}</div>}
    <div className={`gantt-workspace ${assistantHidden ? "assistant-hidden" : ""} ${isFullscreen ? "assistant-unavailable" : ""}`}>
      <main className="gantt-main">
        {!gantt.items.length ? <div className="gantt-empty">No workspace schedule found yet. Generate from project data after assigning stories in Task Studio.</div> : <>
          <GanttChart
            dirty={dirty}
            gantt={gantt}
            humanTeam={humanTeam}
            memberFilter={memberFilter}
            resizing={resizing}
            selectedId={selectedId}
            timelineEnd={timelineEnd}
            timelineStart={timelineStart}
            today={today}
            visibleItems={visibleItems}
            weeks={weeks}
            workspaceView={workspaceView}
            workstreamFilter={workstreamFilter}
            workstreams={workstreams}
            onMemberFilterChange={setMemberFilter}
            onResizeStart={startResize}
            onSelectItem={setSelectedId}
            onUpdateGantt={update}
            onWorkstreamFilterChange={setWorkstreamFilter}
          />
          <GanttEditor
            humanTeam={humanTeam}
            selected={selected}
            taskStudioManaged={taskStudioManaged}
            workspaceView={workspaceView}
            onDelete={deleteEpic}
            onUpdateEffortHours={updateEffortHours}
            onUpdateItem={updateItem}
          />
          {!!gantt.risks.length && <section className="gantt-risks"><strong>Schedule risks</strong>{gantt.risks.map((risk, index) => <span key={index}>{risk}</span>)}</section>}
        </>}
      </main>
      <GanttAssistant
        chat={chat}
        hidden={assistantHidden}
        instruction={instruction}
        isFullscreen={isFullscreen}
        loading={loading}
        hasItems={Boolean(gantt.items.length)}
        onAsk={askAssistant}
        onHide={() => setAssistantHidden(true)}
        onInstructionChange={setInstruction}
        onShow={() => setAssistantHidden(false)}
      />
    </div>
  </div>;
};

export default GanttStudioPage;
